import { createTheme } from '@mui/material';
import { defaultTypography } from './components/typographyStyles';

import { 
  createOutlinedInputStyles, 
  createInputLabelStyles, 
  createFormHelperTextStyles 
} from './components/formStyles';

// Adventure Passion Typography - slightly different from default for more dynamic feel
export const adventurePassionTypography = {
  ...defaultTypography,
  h1: {
    fontWeight: 800, // Extra bold for impact
    letterSpacing: '0.02em',
  },
  h2: {
    fontWeight: 700,
    letterSpacing: '0.02em',
  },
  h3: {
    fontWeight: 700,
    letterSpacing: '0.01em',
  },
  h4: {
    fontWeight: 600,
    letterSpacing: '0.01em',
  },
  button: {
    fontWeight: 700, // Bold buttons for adventure
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  },
};

// Adventure Passion Button Styles
export const createAdventurePassionButtonStyles = (theme: any) => ({
  styleOverrides: {
    root: {
      borderRadius: 12, // More rounded for modern feel
      boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
      textTransform: 'none' as const,
      fontWeight: 700,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        boxShadow: '0 6px 20px rgba(220, 38, 127, 0.4)',
        transform: 'translateY(-2px)',
      },
    },
    contained: {
      '&.MuiButton-containedPrimary': {
        backgroundImage: 'linear-gradient(135deg, #dc267f 0%, #ff6b35 50%, #f72585 100%)',
        color: '#ffffff',
        '&:hover': {
          backgroundImage: 'linear-gradient(135deg, #c41e6f 0%, #e55a2d 50%, #e01a7a 100%)',
        },
      },
      '&.MuiButton-containedSecondary': {
        backgroundImage: 'linear-gradient(135deg, #7209b7 0%, #560bad 50%, #480ca8 100%)',
        color: '#ffffff',
        '&:hover': {
          backgroundImage: 'linear-gradient(135deg, #6207a3 0%, #4a0999 50%, #3e0a95 100%)',
        },
      },
    },
    outlined: {
      borderWidth: 2,
      '&.MuiButton-outlinedPrimary': {
        borderColor: '#dc267f',
        color: '#dc267f',
        '&:hover': {
          borderColor: '#c41e6f',
          backgroundColor: 'rgba(220, 38, 127, 0.08)',
        },
      },
      '&.MuiButton-outlinedSecondary': {
        borderColor: '#7209b7',
        color: '#7209b7',
        '&:hover': {
          borderColor: '#6207a3',
          backgroundColor: 'rgba(114, 9, 183, 0.08)',
        },
      },
    },
  },
});

const baseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#dc267f', // Vibrant magenta-pink for passion
      light: '#ff6b9d', // Lighter passionate pink
      dark: '#a91b5b', // Deep passionate red
    },
    secondary: {
      main: '#7209b7', // Electric purple for creativity
      light: '#a663cc', // Lighter creative purple
      dark: '#4a0570', // Deep creative violet
    },
    background: {
      default: '#fef7ff', // Very light purple-tinted background
      paper: '#ffffff',   // Pure white for contrast
    },
    error: {
      main: '#ff4444', // Bright red for errors
    },
    warning: {
      main: '#ff6b35', // Sunset orange for warnings
    },
    success: {
      main: '#06ffa5', // Electric mint green for success
    },
    info: {
      main: '#00d4aa', // Teal for info
    },
    text: {
      primary: '#2d1b30', // Deep purple-black for readability
      secondary: '#6b4c87', // Medium purple for secondary text
    },
    divider: '#e1bee7', // Light purple divider
    action: {
      active: '#dc267f',
      hover: 'rgba(220, 38, 127, 0.08)',
      selected: 'rgba(220, 38, 127, 0.16)',
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      focus: 'rgba(220, 38, 127, 0.12)',
    },
  },
  typography: adventurePassionTypography,
  shape: {
    borderRadius: 12, // More rounded for modern adventure feel
  },
});

// Adventure Passion Paper Styles
export const createAdventurePassionPaperStyles = (theme: any) => ({
  styleOverrides: {
    root: {
      backgroundImage: 'none',
      '&.MuiPaper-elevation1': {
        boxShadow: '0 4px 20px rgba(220, 38, 127, 0.15)',
      },
      '&.MuiPaper-elevation2': {
        boxShadow: '0 6px 24px rgba(220, 38, 127, 0.2)',
      },
      '&.MuiPaper-elevation3': {
        boxShadow: '0 8px 32px rgba(220, 38, 127, 0.25)',
      },
    },
  },
});

export const createAdventurePassionCardStyles = (theme: any) => ({
  styleOverrides: {
    root: {
      borderRadius: 16,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: '1px solid rgba(220, 38, 127, 0.1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 40px rgba(220, 38, 127, 0.3)',
        borderColor: 'rgba(220, 38, 127, 0.3)',
      },
    },
  },
});

// Create the theme with extensive component overrides
const adventurePassionTheme = createTheme({
  ...baseTheme,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#fef7ff',
          color: '#2d1b30',
          backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(220, 38, 127, 0.05) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(114, 9, 183, 0.05) 0%, transparent 50%)',
          '&::-webkit-scrollbar': {
            width: '12px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#fef7ff',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'linear-gradient(180deg, #dc267f, #7209b7)',
            borderRadius: '6px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'linear-gradient(180deg, #c41e6f, #6207a3)',
          },
        },
        'footer': {
          backgroundColor: '#2d1b30 !important',
          backgroundImage: 'linear-gradient(135deg, #2d1b30 0%, #4a0570 100%) !important',
          color: '#ff6b9d !important',
          borderTop: '3px solid #dc267f !important',
          position: 'relative !important',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            backgroundImage: 'linear-gradient(90deg, #dc267f 0%, #7209b7 50%, #dc267f 100%)',
          },
          '& .MuiTypography-root': {
            color: '#ff6b9d !important',
          },
          '& a.MuiLink-root': {
            color: '#a663cc !important',
          },
          '& a.MuiLink-root:hover': {
            color: '#ff6b9d !important',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiButton: createAdventurePassionButtonStyles(baseTheme),
    MuiPaper: createAdventurePassionPaperStyles(baseTheme),
    MuiCard: createAdventurePassionCardStyles(baseTheme),
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, #2d1b30 0%, #4a0570 50%, #6207a3 100%)',
          boxShadow: '0 4px 20px rgba(220, 38, 127, 0.3)',
          borderBottom: '2px solid #dc267f',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, #2d1b30 0%, #4a0570 50%, #6207a3 100%)',
          color: '#ff6b9d',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#6b4c87',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: 'rgba(220, 38, 127, 0.15)',
            transform: 'scale(1.1)',
          },
          'footer &': {
            color: '#ff6b9d',
            '&:hover': {
              color: '#ffffff',
            }
          },
          '.MuiAppBar-root &': {
            color: '#ff6b9d',
            '&:hover': {
              color: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            '& .MuiAvatar-root': {
              backgroundColor: '#dc267f !important',
              color: '#ffffff !important',
            },
          }
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '.MuiAppBar-root &, .MuiToolbar-root &': {
            color: '#ff6b9d !important',
          },
        },
        h1: { 
          color: '#2d1b30',
          background: 'linear-gradient(135deg, #dc267f, #7209b7)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        },
        h2: { 
          color: '#2d1b30',
          background: 'linear-gradient(135deg, #dc267f, #7209b7)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        },
        h3: { color: '#dc267f' },
        h4: { color: '#7209b7' },
        h5: { color: '#dc267f' },
        h6: { color: '#6b4c87' },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#dc267f !important',
          color: '#ffffff !important',
          '&': {
            backgroundColor: '#dc267f',
            color: '#ffffff',
          },
        },
        colorDefault: {
          backgroundColor: '#dc267f !important',
          color: '#ffffff !important',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#dc267f',
          textDecoration: 'none',
          fontWeight: 600,
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#7209b7',
            textDecoration: 'underline',
          },
          'footer &': {
            color: '#a663cc',
            '&:hover': {
              color: '#ff6b9d',
            }
          }
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(220, 38, 127, 0.2)',
          color: '#2d1b30',
        },
        head: {
          fontWeight: 700,
          color: '#2d1b30',
          backgroundColor: 'rgba(220, 38, 127, 0.05)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(114, 9, 183, 0.03)',
          },
          '&:hover': {
            backgroundColor: 'rgba(220, 38, 127, 0.08)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          '&.MuiChip-colorDefault': {
            backgroundColor: 'rgba(220, 38, 127, 0.1)',
            color: '#dc267f',
          },
        },
        filled: {
          '&.MuiChip-colorPrimary': {
            backgroundImage: 'linear-gradient(135deg, #dc267f, #f72585)',
            color: '#ffffff',
          },
          '&.MuiChip-colorSecondary': {
            backgroundImage: 'linear-gradient(135deg, #7209b7, #a663cc)',
            color: '#ffffff',
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: '#ff4444',
          color: '#ffffff',
          fontWeight: 700,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: 'rgba(6, 255, 165, 0.1)',
          color: '#2d1b30',
          border: '1px solid rgba(6, 255, 165, 0.3)',
        },
        standardInfo: {
          backgroundColor: 'rgba(0, 212, 170, 0.1)',
          color: '#2d1b30',
          border: '1px solid rgba(0, 212, 170, 0.3)',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 107, 53, 0.1)',
          color: '#2d1b30',
          border: '1px solid rgba(255, 107, 53, 0.3)',
        },
        standardError: {
          backgroundColor: 'rgba(255, 68, 68, 0.1)',
          color: '#2d1b30',
          border: '1px solid rgba(255, 68, 68, 0.3)',
        },
      },
    },
    MuiOutlinedInput: createOutlinedInputStyles(baseTheme),
    MuiInputLabel: createInputLabelStyles(baseTheme),
    MuiFormHelperText: createFormHelperTextStyles(baseTheme),
  },
});

export default adventurePassionTheme; 